<template>
  <div class="card-body table mb-0">

    <PageHeader v-if="showHeading"
      title="All Invoices"
      class="poppins fw-normal text-nowrap mt-2"
    />

    <!-- INVOICES TABLE -->
    <GeneralTable
      :isProcessing="processing"
      :items="items"
      :fields="fields"
      :showEyeButton="true"
      :showActionButton="false"
      @showItem="openInvoiceModal"
    />

    <!-- INVOICE MODAL -->
    <b-modal ref="invoiceModal" hide-footer size="xl">
      <template #modal-header="{ close }">
        <div class="d-flex align-items-center w-100 invoice-modal-header">

          <h5 class="mb-0 fw-semibold">
            Invoice Details
          </h5>
          <div class="d-flex align-items-center ms-auto gap-3">

            <a
              v-if="selectedInvoice?.invoice_pdf"
              :href="selectedInvoice.invoice_pdf"
              target="_blank"
              class="invoice-link"
            >
              📄 View Invoice PDF
            </a>

            <a
              v-if="selectedInvoice?.hosted_invoice_url"
              :href="selectedInvoice.hosted_invoice_url"
              target="_blank"
              class="invoice-link"
            >
              🌐 Invoice on Stripe link
            </a>

            <button
              type="button"
              class="btn-close custom-close"
              @click="close()"
            ></button>

          </div>
        </div>
      </template>
      <div v-if="selectedInvoice" class="invoice-info">
        <div class="invoice-column">
          <p><strong>Invoice #:</strong> {{ selectedInvoice.number ?? "N/A" }}</p>
          <p><strong>Status:</strong> {{ selectedInvoice.status }}</p>
          <p><strong>Currency:</strong> {{ selectedInvoice.currency?.toUpperCase() }}</p>

          <p><strong>Email:</strong> {{ selectedInvoice.customer_email }}</p>
          <p><strong>Receipt #:</strong> {{ selectedInvoice.receipt_number ?? '—' }}</p>

          <p><strong>Billing Reason:</strong> {{ selectedInvoice.billing_reason }}</p>
          <p><strong>Collection Method:</strong> {{ selectedInvoice.collection_method }}</p>
          <p><strong>Paid:</strong> {{ selectedInvoice.paid ? 'Yes' : 'No' }}</p>
        </div>

        <!-- RIGHT COLUMN -->
        <div class="invoice-column">
          <p><strong>Total:</strong> {{ formatAmount(selectedInvoice.total) }}</p>
          <p><strong>Amount Paid:</strong> {{ formatAmount(selectedInvoice.amount_paid) }}</p>
          <p><strong>Amount Remaining:</strong> {{ formatAmount(selectedInvoice.amount_remaining) }}</p>

          <p><strong>Created At:</strong> {{ formatDate(selectedInvoice.created) }}</p>
          <p><strong>Period:</strong>
            {{ getInvoicePeriod(selectedInvoice) }}
          </p>

          <p><strong>Charge ID:</strong> {{ selectedInvoice.charge }}</p>
          <p><strong>Payment Intent:</strong> {{ selectedInvoice.payment_intent }}</p>
        </div>

        <!-- LINE ITEMS -->
        <div class="invoice-column full-width">
          <h6>Line Items</h6>

          <div
            v-for="line in selectedInvoice.lines?.data"
            :key="line.id"
            class="line-item"
          >
            <p class="mb-1"><strong>{{ line.description }}</strong></p>
            <small>
              {{ formatAmount(line.amount) }} × {{ line.quantity }}
            </small>
          </div>
        </div>

        <!-- LINKS -->
        <!-- <div class="invoice-column full-width">
          <a
            v-if="selectedInvoice.invoice_pdf"
            :href="selectedInvoice.invoice_pdf"
            target="_blank"
          >
            📄 View Invoice PDF
          </a>
          <br />
          <a
            v-if="selectedInvoice.hosted_invoice_url"
            :href="selectedInvoice.hosted_invoice_url"
            target="_blank"
          >
            🌐 Invoice on Stripe link 
          </a>
        </div> -->

      </div>
    </b-modal>

  </div>
</template>

<script>
import GeneralTable from "@/components/MagicTable/GeneralTable.vue";
import PageHeader from "@/components/static/page-header";

export default {
  name: "UserInvoices",

  components: {
    GeneralTable,
    PageHeader
  },

  props: {
    user: {
      type: Object,
      required: true
    },
    showHeading:{
        type: Boolean,
        default:true
    }
  },

  data() {
    return {
      processing: false,

      items: [],
      selectedInvoice: null,

      fields: [
        { key: "number", label: "Invoice #" },
        { key: "customer_email", label: "Customer Email" },
        { key: "billing_reason", label: "Invoice Reason" },
        { key: "billing_description", label: "Invoice Description" },
        { key: "period", label: "Period" },
        { key: "status", label: "Status" },
        { key: "discount", label: "Discount" },
        { key: "total_formatted", label: "Total" },
        { key: "created_at", label: "Created At" },
        { key: "action" }
      ]
    };
  },

  mounted() {
    this.fetchInvoices();
  },

  methods: {
    fetchInvoices() {
      this.processing = true;

      this.$axios
        .get("invoices/current-user-invoices", {
          params: { user_id: this.user.id }
        })
        .then((res) => {
          const invoices = res.data;

          this.items = invoices.map(inv => ({
            ...inv, // KEEP FULL OBJECT
            number:inv.number ?? "Not Charged Yet",
            id: inv.id,
            total_formatted: this.formatAmount(inv.total),
            created_at: this.formatDate(inv.created),
            billing_reason: inv.billing_reason,
            billing_description: inv?.lines?.data[0]?.description,
            period: this.getInvoicePeriod(inv),
            discount: inv.discount?.coupon ? this.getDiscountDetails(inv.discount) : "No Discount",
          }));

          this.processing = false;
        })
        .catch(() => {
          this.processing = false;
        });
    },
    getInvoicePeriod(inv){
        let time_period = inv?.lines?.data[0]?.period ? this.formatDate(inv?.lines?.data[0]?.period.start) + " → " + this.formatDate(inv?.lines?.data[0]?.period.end) : "—";

        if(inv?.lines?.data[0]?.metadata?.billing_model === "calendar_year_initial"){
          time_period = this.formatDate(inv?.lines?.data[0]?.metadata?.access_start) + " → " + this.formatDate(inv?.lines?.data[0]?.metadata?.access_end);
        }

        return time_period;
    },

    getDiscountDetails(discount) {
      let amount = discount.coupon.amount_off != null ? this.formatAmount(discount.coupon.amount_off) : discount.coupon.percent_off + "%";
      return amount;
    },
   
    openInvoiceModal(invoice_id) {
      this.selectedInvoice = this.items.find(inv => inv.id === invoice_id);
      this.$refs.invoiceModal.show();
    },

   
    formatAmount(amount = 0) {
      return `$${(amount / 100).toFixed(2)}`;
    },

    formatDate(timestamp) {
      if (!timestamp) return "—";
      return new Date(timestamp * 1000).toLocaleString();
    }
  }
};
</script>

<style scoped>
.invoice-info {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.invoice-column {
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 12px;
}

.full-width {
  grid-column: span 2;
}

.line-item {
  border-bottom: 1px dashed #ccc;
  padding-bottom: 6px;
  margin-bottom: 6px;
}

@media (max-width: 768px) {
  .invoice-info {
    grid-template-columns: 1fr;
  }
  .full-width {
    grid-column: span 1;
  }
}
</style>
